import React, { useCallback, useEffect, useRef, useState } from 'react';

import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Autocomplete, CircularProgress, IconButton, Stack, TextField, Typography } from '@mui/material';

import { type AssetMetadata, getAssetsSignedUrls, searchAssets } from '~/services/VideoGenService';

interface AudioPickerProps {
  value: string;
  onChange: (assetId: string, audioUrl: string | null) => void;
}

export default function AudioPicker({ value, onChange }: AudioPickerProps) {
  const [options, setOptions] = useState<AssetMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedOption, setSelectedOption] = useState<AssetMetadata | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const fetchAssets = useCallback(async (name?: string) => {
    setLoading(true);
    try {
      const response = await searchAssets({
        type: 'AUDIO',
        status: ['ACTIVE'],
        name: name || undefined,
        page: 1,
        limit: 20,
      });
      setOptions(response.result);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchAssets(searchText);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchText, fetchAssets]);

  useEffect(() => {
    if (value && options.length > 0 && !selectedOption) {
      const match = options.find((o) => o.id === value);
      if (match) {
        setSelectedOption(match);
      }
    }
  }, [value, options, selectedOption]);

  const resolveUrl = useCallback(
    async (assetId: string): Promise<string | null> => {
      if (previewUrls[assetId]) return previewUrls[assetId];
      try {
        const response = await getAssetsSignedUrls({ assetIds: [assetId] });
        const url = response.result[assetId];
        if (url) {
          setPreviewUrls((prev) => ({ ...prev, [assetId]: url }));
          return url;
        }
      } catch {
        /* ignore */
      }
      return null;
    },
    [previewUrls],
  );

  const handleTogglePreview = useCallback(
    async (e: React.MouseEvent, assetId: string) => {
      e.stopPropagation();

      if (previewingId === assetId) {
        audioRef.current?.pause();
        setPreviewingId(null);
        return;
      }

      const url = await resolveUrl(assetId);
      if (!url) return;

      if (audioRef.current) {
        audioRef.current.pause();
      }

      const audio = new Audio(url);
      audio.addEventListener('ended', () => setPreviewingId(null));
      audioRef.current = audio;
      audio.play();
      setPreviewingId(assetId);
    },
    [previewingId, resolveUrl],
  );

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  const handleSelect = useCallback(
    async (asset: AssetMetadata | null) => {
      setSelectedOption(asset);
      audioRef.current?.pause();
      setPreviewingId(null);

      if (!asset) {
        onChange('', null);
        return;
      }

      const url = await resolveUrl(asset.id);
      onChange(asset.id, url);
    },
    [onChange, resolveUrl],
  );

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      loading={loading}
      getOptionLabel={(option) => option.name || option.id}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onInputChange={(_event, newValue, reason) => {
        if (reason === 'input') {
          setSearchText(newValue);
        }
      }}
      onChange={(_event, newValue) => {
        handleSelect(newValue);
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
            <IconButton size="small" onClick={(e) => handleTogglePreview(e, option.id)} sx={{ flexShrink: 0 }}>
              {previewingId === option.id ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
            <Stack sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" noWrap>
                {option.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.id}
              </Typography>
            </Stack>
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Audio Asset *"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
